import { create } from "zustand";
import type { QueryClient } from "@tanstack/react-query";
import { REQUEST_TIMEOUT_CODE, RivetsApiError, type AttachmentRef, type ChatDetailResponse, type ChatEvent, type ProviderSelection } from "@rivets/core";
import { client } from "@/lib/api";
import { perfMeasure } from "@/lib/perf";
import { qk } from "@/lib/query-keys";
import { workerAccessErrorText } from "@/features/workers/worker-access";
import { onConfirmedEvents } from "./chatStore";
import { isOutcomeUncertain } from "@/lib/outcome";
import { useChatComposers } from "./chatComposerStore";

/**
 * Messages sent from a chat's composer that the transcript has not echoed yet.
 *
 * The bubble is drawn the moment Send is pressed, with the request's
 * `clientRequestId`. The server stores that id on the `user.message` it
 * appends, so the echo settles the bubble exactly — repeated text, a queued
 * steer or a slow POST cannot confuse two messages. A failed POST leaves the
 * bubble in place with an inline Retry, which resends under the same id (the
 * server treats it as the same request), and a Discard.
 *
 * Three looks for three states (CONTRACT C3): `sending` (on its way, muted
 * with a spinner), `accepted` (the server took it; only the echo, which is
 * delivery to the agent, is outstanding) and `failed`. They are written to
 * this browser with the drafts (`draftPersistence`), so a reload neither
 * loses one nor sends it twice: an in-flight one goes again under its id, an
 * accepted one waits for its echo.
 */
export interface PendingSend {
  clientRequestId: string;
  chatId: string;
  userId?: string;
  text: string;
  provider: ProviderSelection;
  attachments?: AttachmentRef[];
  /** Team messages this prompt carries to the agent. Part of the request, so
   * a retry carries the same discussion the first attempt did. */
  teamContext?: string[];
  createdAt: number;
  state: "sending" | "accepted" | "failed";
  error?: string;
}

interface PendingSendsState {
  byChat: Record<string, PendingSend[]>;
  add: (send: PendingSend) => void;
  /** The POST succeeded: the server holds it, and only the echo is owed. */
  accept: (chatId: string, clientRequestId: string) => void;
  fail: (chatId: string, clientRequestId: string, error: string) => void;
  retrying: (chatId: string, clientRequestId: string) => void;
  drop: (chatId: string, clientRequestId: string) => void;
  /** Removes every pending send the confirmed events echo. */
  settle: (chatId: string, events: readonly ChatEvent[]) => void;
  /** Replaces every pending send (an organization's saved ones loading). */
  restore: (byChat: Record<string, PendingSend[]>) => void;
  reset: () => void;
}

const EMPTY: PendingSend[] = [];

function update(state: PendingSendsState, chatId: string, next: PendingSend[]): Pick<PendingSendsState, "byChat"> {
  const byChat = { ...state.byChat };
  if (next.length === 0) delete byChat[chatId];
  else byChat[chatId] = next;
  return { byChat };
}

/** The `clientRequestId`s a batch of confirmed events echoes. */
export function echoedRequestIds(events: readonly ChatEvent[]): Set<string> {
  const ids = new Set<string>();
  for (const event of events) {
    if (event.payload.kind === "user.message" && event.payload.clientRequestId) ids.add(event.payload.clientRequestId);
  }
  return ids;
}

export const usePendingSends = create<PendingSendsState>((set) => ({
  byChat: {},
  add: (send) => set((state) => update(state, send.chatId, [...(state.byChat[send.chatId] ?? EMPTY), send])),
  accept: (chatId, clientRequestId) => set((state) => {
    const list = state.byChat[chatId];
    if (!list?.some((send) => send.clientRequestId === clientRequestId && send.state !== "accepted")) return state;
    return update(state, chatId, list.map((send) => send.clientRequestId === clientRequestId ? { ...send, state: "accepted", error: undefined } : send));
  }),
  fail: (chatId, clientRequestId, error) => set((state) => {
    const list = state.byChat[chatId];
    if (!list?.some((send) => send.clientRequestId === clientRequestId)) return state;
    return update(state, chatId, list.map((send) => send.clientRequestId === clientRequestId ? { ...send, state: "failed", error } : send));
  }),
  retrying: (chatId, clientRequestId) => set((state) => {
    const list = state.byChat[chatId];
    if (!list) return state;
    return update(state, chatId, list.map((send) => send.clientRequestId === clientRequestId ? { ...send, state: "sending", error: undefined } : send));
  }),
  drop: (chatId, clientRequestId) => set((state) => {
    const list = state.byChat[chatId];
    if (!list) return state;
    return update(state, chatId, list.filter((send) => send.clientRequestId !== clientRequestId));
  }),
  settle: (chatId, events) => set((state) => {
    const list = state.byChat[chatId];
    if (!list) return state;
    const echoed = echoedRequestIds(events);
    if (echoed.size === 0) return state;
    const kept = list.filter((send) => !echoed.has(send.clientRequestId));
    return kept.length === list.length ? state : update(state, chatId, kept);
  }),
  restore: (byChat) => set({ byChat }),
  reset: () => set({ byChat: {} }),
}));

/** Whether anything is on its way that a closed page would leave unsent. */
export function hasSendsInFlight(): boolean {
  return Object.values(usePendingSends.getState().byChat).some((list) => list.some((send) => send.state === "sending"));
}

let recoverChat: (chatId: string) => void = () => undefined;
/** How a chat is read back when a send's outcome is unknown: the socket layer
 * resubscribes it from its cursor, so the echo arrives if the message did. */
export function setPendingSendRecovery(recover: (chatId: string) => void): void {
  recoverChat = recover;
}

/** Reads a chat back to find out whether a send landed. */
export function recoverPendingSendChat(chatId: string): void {
  recoverChat(chatId);
}

export function selectPendingSends(chatId: string) {
  return (state: PendingSendsState) => state.byChat[chatId] ?? EMPTY;
}

/**
 * Where each pending send is drawn: where its echo is going to land, so Send
 * puts it in its final place and the echo only settles it.
 *
 * Into a run that is going, a message waits behind the agent's turn, so it goes
 * straight to the queue above the composer. Otherwise it opens a run and is
 * drawn in the transcript — and every send after it, while it is on its way,
 * queues behind that run. A failed send opens nothing. `runStartsAt` is when
 * the send that opens a run was pressed, which is when the chat starts working
 * as far as its reader is concerned.
 */
export interface PendingPlacement {
  transcript: PendingSend[];
  queued: PendingSend[];
  runStartsAt?: number;
}

export function placePendingSends(sends: readonly PendingSend[], runOpen: boolean): PendingPlacement {
  const placement: PendingPlacement = { transcript: [], queued: [] };
  let open = runOpen;
  for (const send of sends) {
    if (open) {
      placement.queued.push(send);
      continue;
    }
    placement.transcript.push(send);
    if (send.state !== "failed") {
      open = true;
      placement.runStartsAt = send.createdAt;
    }
  }
  return placement;
}

/** What the live timer remembers between renders. */
export interface HeldRunStart {
  /** The confirmed run's own start, once one is showing. */
  run?: number;
  /** When the send that opened it was pressed. */
  pressedAt?: number;
}

/**
 * The time the "Working" timer counts from. It starts at the press, before the
 * server has said anything, and keeps counting from the press once the run's
 * status lands: jumping to the server's later stamp would run the timer
 * backwards. The press is this device's clock, like the `Date.now()` the timer
 * reads, so it is also the more accurate of the two. A run this client did not
 * open counts from its own start.
 */
export function holdRunStart(
  held: HeldRunStart,
  runStartedAt: number | undefined,
  pressedAt: number | undefined,
): { held: HeldRunStart; startedAt?: number } {
  if (runStartedAt === undefined) return { held: { pressedAt }, startedAt: pressedAt };
  if (held.run === runStartedAt) return { held, startedAt: held.pressedAt ?? runStartedAt };
  // A new run is the one the press opened only if no run was showing before it.
  const pressed = held.run === undefined ? held.pressedAt : undefined;
  return { held: { run: runStartedAt, pressedAt: pressed }, startedAt: pressed ?? runStartedAt };
}

/** POSTs a pending send. A retry is the same request (same
 * `clientRequestId`), which the server deduplicates. */
export async function deliverPendingSend(
  queryClient: QueryClient,
  send: PendingSend,
  api: Pick<typeof client, "sendMessage"> = client,
): Promise<void> {
  usePendingSends.getState().retrying(send.chatId, send.clientRequestId);
  try {
    await api.sendMessage(send.chatId, {
      text: send.text,
      provider: send.provider,
      clientRequestId: send.clientRequestId,
      ...(send.attachments ? { attachments: send.attachments } : {}),
      ...(send.teamContext?.length ? { teamContext: { messageIds: send.teamContext } } : {}),
    });
    usePendingSends.getState().accept(send.chatId, send.clientRequestId);
    queryClient.setQueryData<ChatDetailResponse>(qk.chat(send.chatId), (current) => current
      ? { ...current, chat: { ...current.chat, provider: send.provider } }
      : current);
  } catch (error) {
    if (error instanceof RivetsApiError && error.code === REQUEST_TIMEOUT_CODE) {
      // Our own deadline: it may or may not have landed. "Accepted" would
      // never resolve if it did not, so it reads as not sent, with a Retry
      // that is the same request, while the chat is read back; its echo, if
      // it landed, settles it (review #17).
      usePendingSends.getState().fail(send.chatId, send.clientRequestId, "Couldn't confirm this was sent. Retry sends it once.");
      recoverChat(send.chatId);
      return;
    }
    if (isOutcomeUncertain(error)) {
      // It may have landed (CONTRACT P7): neither "failed" nor a second send.
      // It reads as accepted and the chat is read back; its echo settles it.
      usePendingSends.getState().accept(send.chatId, send.clientRequestId);
      recoverChat(send.chatId);
      return;
    }
    usePendingSends.getState().fail(
      send.chatId,
      send.clientRequestId,
      workerAccessErrorText(error) ?? (error instanceof RivetsApiError ? error.message : "Could not send this message."),
    );
  }
}

/** Drops a failed send. Its text goes back into the composer when that is
 * empty, so a discarded message is not lost for having failed once. */
export function discardPendingSend(send: PendingSend): void {
  usePendingSends.getState().drop(send.chatId, send.clientRequestId);
  const composers = useChatComposers.getState();
  if (!(composers.composers[send.chatId]?.text ?? "").trim()) composers.setText(send.chatId, send.text);
}

// Settled in the same update that adds the echo, so the bubble and its echo
// are never on screen together.
onConfirmedEvents((chatId, events) => {
  for (const clientRequestId of echoedRequestIds(events)) perfMeasure("rivets:send→echo", `rivets:send-echo:${clientRequestId}`);
  usePendingSends.getState().settle(chatId, events);
});
